'use client';

import React from 'react';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';


const GUARANTEES = [
  {
    id: 0,
    title: '100% Guaranteed',
    description:
      'Every Green Filter is backed by our lifetime warranty. If it ever fails due to defects in materials or workmanship, we will replace it free of charge.',
    icon: '/assets/icons8-wheel-100_1icons8-wheel-100.webp',
  },
  {
    id: 1,
    title: 'Shipping & Returns',
    description:
      'Free shipping on orders over $100. Not satisfied? Return within 30 days for a full refund. We stand behind our products 100%.',
    icon: '/assets/icons8-free-shipping-100_1icons8-free-shipping-100.webp',
  },
  {
    id: 2,
    title: 'Customer Service',
    description:
      'Our team of filter experts is here to help you find the perfect filter for your vehicle. Contact us anytime with questions or concerns.',
    icon: '/assets/icons8-communication-100_1icons8-communication-100.webp',
  },
];

const HomeElement6 = () => {
  return (
    <Section>
      <Container>
        <HeadingBlock>
          <Headline>The Green Filter Guarantee</Headline>
          <Accent />
        </HeadingBlock>

        <GuaranteesGrid>
          {GUARANTEES.map((item) => (
            <GuaranteeCard key={item.id}>
              <IconContainer>
                <Icon src={item.icon} alt={item.title} />
              </IconContainer>
              <CardTitle>{item.title}</CardTitle>
              <CardText>{item.description}</CardText>
            </GuaranteeCard>
          ))}
        </GuaranteesGrid>

        <DisclaimerBox>
          <DisclaimerText>
            <strong>Important:</strong> All filters are inspected before shipment. If you
            receive a defective filter, please contact us within 48 hours of delivery. Filters
            that have been installed, used, or oiled cannot be returned unless defective.
            Original packaging must be retained for returns.
          </DisclaimerText>
        </DisclaimerBox>
      </Container>
    </Section>
  );
};

export default React.memo(HomeElement6);

const Section = styled.section`
  background: #fff;
  padding: 80px 24px;

  @media (max-width: 1024px) {
    padding: 64px 24px;
  }

  @media (max-width: 767px) {
    padding: 48px 16px;
  }
`;

const Container = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const HeadingBlock = styled.div`
  max-width: 640px;
  margin: 0 auto 56px;
  text-align: center;

  @media (max-width: 767px) {
    margin-bottom: 40px;
  }
`;

const Headline = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0 0 14px;
  line-height: 1.2;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const Accent = styled.div`
  width: 56px;
  height: 3px;
  background: ${COLORS.brandGreen};
  border-radius: 2px;
  margin: 0 auto;
`;

const GuaranteesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 48px;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const GuaranteeCard = styled.div`
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: ${COLORS.brandGreen};
  }

  @media (max-width: 767px) {
    padding: 28px 20px;
  }
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 767px) {
    width: 70px;
    height: 70px;
  }
`;

const Icon = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const CardTitle = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px;
  line-height: 1.3;

  @media (max-width: 767px) {
    font-size: 18px;
  }
`;

const CardText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #555;
  margin: 0;
  line-height: 1.65;

  @media (max-width: 767px) {
    font-size: 13px;
  }
`;

const DisclaimerBox = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  padding: 24px 28px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${COLORS.brandGreen}, #00c728);
  }

  @media (max-width: 767px) {
    padding: 20px;
  }
`;

const DisclaimerText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #495057;
  margin: 0;
  line-height: 1.6;

  strong {
    font-weight: 700;
    color: #212529;
  }

  @media (max-width: 767px) {
    font-size: 12px;
  }
`;
